import { getHomePageDataByUrl } from '@/api/content';
import ImpTracker from '@/components/common/ImpTracker';
import useImpressionT2 from '@/tracks/useImpressionT2';
import { CmsSsrComponentProps } from '@/types/cms';
import { isValidArray } from '@/utils/isValidArray';
import useTranslation from 'next-translate/useTranslation';
import { FC, useCallback, useEffect, useState } from 'react';
import Container from './Container';

const RaffleOrderList: FC<CmsSsrComponentProps> = props => {
  const {
    fetchUrl,
    properties,
    componentKey,
    componentInstanceKey,
    componentPosition,
    trackingContext,
    staticRes,
    top,
    onDataSourceError
  } = props;
  const { bannerImpression } = useImpressionT2();

  const { t } = useTranslation('common');
  const [data, setData] = useState<any[]>();
  const [load, setLoad] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await getHomePageDataByUrl(fetchUrl);
      if (res?.result && isValidArray(res.object)) {
        setData(res.object);
      } else {
        setData([]);
        onDataSourceError?.();
      }
      setLoad(false);
    } catch (error) {
      setData([]);
      onDataSourceError?.();
      setLoad(false);
    }
  }, [fetchUrl, onDataSourceError]);

  useEffect(() => {
    if (staticRes) {
      setData(staticRes ?? []);
      setLoad(true);
      return;
    }
    fetchData();
  }, [fetchData, staticRes]);

  if (!fetchUrl) return <></>;

  if (load || !Array.isArray(data) || data.length === 0) return <></>;

  const { order_bg_color = '#fff', title = '', title_style = '{}', custom_json = '{}' } = properties || {};

  let titleStyle = {};
  let customJson: Record<string, string> = {};

  try {
    titleStyle = title_style ? JSON.parse(title_style as string) : {};
    customJson = custom_json ? JSON.parse(custom_json as string) : {};
  } catch (error) {
    console.log('parse error', error);
  }

  const {
    fontColor = '#111',
    borderColor = '#fff',
    titleLineColor = '#fff',
    lineColor = '#fff',
    lotteryLable = t('raffle_lottery_number'),
    orderLabel = t('raffle_order_number'),
    titleLineMode = 'dot',
    subTitle = ''
  } = customJson;

  return (
    <ImpTracker
      impTrackingT2={() => {
        bannerImpression({
          mod_nm: componentKey,
          mod_pos: componentPosition,
          ctx: trackingContext
        });
      }}>
      <Container
        aria-label={properties?.title}
        componentKey={componentKey}
        componentInstanceKey={componentInstanceKey}
        properties={properties}
        className="py-2"
        top={top}
        style={{ backgroundColor: customJson?.sectionBgColor }}>
        <div
          id="tickets"
          className="pt-6 px-5 pb-0 flex items-center justify-center flex-col relative my-0 mx-auto text-[#000] tracking-tight text-center">
          <div className="font-normal leading-[1.2] mb-2" style={{ ...titleStyle }}>
            <div className="text-[28px] ">{title as string}</div>
            {subTitle && <div className="text-[14px]">{subTitle}</div>}
          </div>
          <div
            style={{
              borderColor: borderColor,
              backgroundColor: order_bg_color as string,
              color: fontColor
            }}
            className="flex flex-col items-center p-5 w-full rounded-[10px] text-center box-border mx-4"
            data-role="ticketBox">
            <div className="flex items-center justify-around text-sm font-medium leading-[125%] h-7 mt-1 w-full mb-2">
              <div className="flex justify-center w-1/2">{lotteryLable}</div>
              <div className="flex justify-center w-1/2">{orderLabel}</div>
            </div>
            <div
              className="h-[10px]" // 容器高度略高于圆点
              style={
                titleLineMode === 'dot'
                  ? {
                      width: 'calc(100% - 40px)',
                      backgroundImage: `radial-gradient(circle at center, ${titleLineColor} 1.5px, transparent 2px)`,
                      backgroundSize: '14px 8px', // 控制圆点间距：第一个值 8px 是圆心到圆心的水平距离
                      backgroundRepeat: 'repeat-x',
                      height: '8px' // 圆点高度 4px
                    }
                  : {
                      width: '100%',
                      height: '1px',
                      backgroundColor: titleLineColor
                    }
              }
            />
            {data.map((order, index) => {
              return (
                <div
                  style={{
                    backgroundColor: index % 2 !== 0 ? lineColor : ''
                  }}
                  className="flex items-center justify-around text-sm font-normal leading-[125%] h-7 mt-1 w-full"
                  key={`lottery-${order?.lottery_id}`}>
                  <div className="flex justify-center w-1/2">{order?.lottery_id}</div>
                  <div className="flex justify-center w-1/2">{order?.order_id}</div>
                </div>
              );
            })}
          </div>
        </div>
      </Container>
    </ImpTracker>
  );
};

export default RaffleOrderList;
